/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import WireframeBase, { type ComponentStyles } from './WireframeBase';
import tools from '../tools';
import wireframePill from '../assets/icons/wireframePill.svg';
import wireframeStateRoundHollow from '../assets/icons/wireframeStateRoundHollow.svg';
import wireframeStateSquareHollow from '../assets/icons/wireframeStateSquareHollow.svg';
import { SPACER_1, SPACER_3, SPACER_6 } from '../utils/style';
import WireframeLinearResizeControl from '../controls/WireframeLinearResizeControl';
import { WIREFRAME_PRIMARY_COLOR } from '../utils/wireframes';
import {
  FlexColumn,
  type FlexColumnProps,
} from '@lib/canvasx/components/FlexColumn';
import { Placeholder } from '../components/wireframes/Placeholder';

const ROUND_RADIUS = SPACER_6;
const SQUARE_RADIUS = SPACER_1;
const PADDING_VERTICAL = SPACER_1;
const PADDING_HORIZONTAL = SPACER_3;

class WireframePill extends WireframeBase {
  constructor(fabricObject) {
    super(fabricObject);
    if (!this.fabricObject.style) {
      this.fabricObject.set('style', 'round');
    }
  }

  initItems() {
    if (!this.placeholder?.value) {
      this.addPlaceholder({ value: 'Pill text' });
    }
  }

  tools() {
    return [
      [tools.ToolWireframeComponentStyle],
      [tools.ToolPillStyleFlags],
      [tools.ToolSize],
      [tools.ToolWireframeIcon],
      [tools.ToolFillColorWithoutOpacity],
    ];
  }

  static iconSvg() {
    return wireframePill;
  }

  static externalName(): string {
    return 'Pill';
  }

  static wireframeControls = this.createDefaultControls();
  static createDefaultControls() {
    const controls = super.createDefaultControls();
    return {
      ...controls,
      ml: new WireframeLinearResizeControl({ edge: 'left' }),
      mr: new WireframeLinearResizeControl({ edge: 'right' }),
    };
  }

  static componentStyles(): ComponentStyles {
    return {
      round: wireframeStateRoundHollow,
      square: wireframeStateSquareHollow,
    };
  }

  componentStyles(): ComponentStyles {
    return WireframePill.componentStyles();
  }

  minDimension() {
    const { placeholder } = this.fabricObject;
    const hasIcon = placeholder.iconLeft || placeholder.iconRight;
    const fallback = PADDING_HORIZONTAL * 2 + (hasIcon ? SPACER_3 : 0);
    return this.maxItemDimensions?.width ?? fallback;
  }

  render(ctx: CanvasRenderingContext2D) {
    const { width, scaleX, fontSize, showBackground, showOutline } =
      this.fabricObject;
    const fill = this.fabricObject.fill ?? WIREFRAME_PRIMARY_COLOR;
    const borderRadius = this.round ? ROUND_RADIUS : SQUARE_RADIUS;

    let renderedDimensions;

    const props: Partial<FlexColumnProps> = {};

    if (this.fabricObject.userHasExplicitlySetDimensions()) {
      props.width = width;
    }

    if (showBackground) {
      props.fill = { color: fill, opacity: 0.08 };
    }

    if (showOutline) {
      props.border = fill;
    }

    this.resetMaxItemDimensions();

    withContext(
      {
        canvas: this.fabricObject.canvas,
        ctx,
        object: this.fabricObject,
        scale: scaleX,
      },
      () => {
        renderElement(
          <FlexColumn
            horizontalAlignment='center'
            borderRadius={borderRadius}
            setRenderedDimensions={dimensions => {
              renderedDimensions = dimensions;
            }}
            {...props}
          >
            <Placeholder
              wireframe={this}
              padding={{
                left: PADDING_HORIZONTAL,
                right: PADDING_HORIZONTAL,
                top: PADDING_VERTICAL + 1,
                bottom: PADDING_VERTICAL,
              }}
              color={fill}
              size={fontSize}
              setRenderedDimensions={dimensions => {
                this.updateMaxItemDimensions({
                  dimensions,
                  useUnwrappedWidth: true,
                });
              }}
            />
          </FlexColumn>
        );

        this._updateRenderedDimensions(renderedDimensions);
      }
    );
  }
}

export default WireframePill;
